/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Endpoints} from '@app/Endpoints';
import http from '@app/lib/HttpClient';
import {Logger} from '@app/lib/Logger';
import type {
	LocalizedCardPreapprovalContinueResponse,
	PricingMode,
	PriceIdsResponse,
} from '@fluxer/schema/src/domains/premium/PremiumSchemas';

const logger = new Logger('Premium');
const PRICE_IDS_CACHE_TTL_MS = 5 * 60 * 1000;
export type PriceIds = PriceIdsResponse;

interface CachedPriceIdsEntry {
	value?: PriceIds;
	fetchedAt?: number;
	promise?: Promise<PriceIds>;
}
const priceIdsCache = new Map<string, CachedPriceIdsEntry>();

export async function fetchPriceIds(countryCode?: string, pricingMode: PricingMode = 'localized'): Promise<PriceIds> {
	const normalizedCountryCode = countryCode?.toUpperCase();
	const cacheKey = `${normalizedCountryCode ?? 'default'}:${pricingMode}`;
	const cachedEntry = priceIdsCache.get(cacheKey);
	if (cachedEntry?.value && cachedEntry.fetchedAt && Date.now() - cachedEntry.fetchedAt < PRICE_IDS_CACHE_TTL_MS) {
		return cachedEntry.value;
	}

	if (cachedEntry?.promise) {
		return cachedEntry.promise;
	}

	const request = (async () => {
			try {
				const response = await http.get<PriceIds>({
					url: Endpoints.PREMIUM_PRICE_IDS,
					query: {
						...(normalizedCountryCode ? {country_code: normalizedCountryCode} : {}),
						pricing_mode: pricingMode,
					},
				});
				logger.debug('Price IDs fetched', response.body);
			priceIdsCache.set(cacheKey, {
				value: response.body,
				fetchedAt: Date.now(),
			});
			return response.body;
		} catch (error) {
			priceIdsCache.delete(cacheKey);
			logger.error('Price IDs fetch failed', error);
			throw error;
		}
	})();

	priceIdsCache.set(cacheKey, {promise: request});

	try {
		return await request;
	} catch (error) {
		throw error;
	}
}

export async function createCustomerPortalSession(): Promise<string> {
	try {
		const response = await http.post<{url: string}>(Endpoints.PREMIUM_CUSTOMER_PORTAL);
		logger.info('Customer portal session created');
		return response.body.url;
	} catch (error) {
		logger.error('Customer portal session creation failed', error);
		throw error;
	}
}

export async function createCheckoutSession(
	priceId: string,
	countryCode?: string,
	isGift: boolean = false,
	pricingMode: PricingMode = 'localized',
): Promise<string> {
	try {
		const url = isGift ? Endpoints.STRIPE_CHECKOUT_GIFT : Endpoints.STRIPE_CHECKOUT_SUBSCRIPTION;
		const response = await http.post<{url: string}>(url, {
			price_id: priceId,
			...(countryCode ? {country_code: countryCode.toUpperCase()} : {}),
			pricing_mode: pricingMode,
		});
		logger.info('Checkout session created', {priceId, countryCode, isGift, pricingMode});
		return response.body.url;
	} catch (error) {
		logger.error('Checkout session creation failed', error);
		throw error;
	}
}

export async function createLocalizedCardPreapprovalSession(
	priceId: string,
	countryCode: string,
	pricingMode: PricingMode = 'localized',
): Promise<string> {
	try {
		const response = await http.post<{url: string}>(Endpoints.STRIPE_CHECKOUT_SUBSCRIPTION_PREAPPROVAL, {
			price_id: priceId,
			country_code: countryCode.toUpperCase(),
			pricing_mode: pricingMode,
		});
		logger.info('Localized card preapproval session created', {priceId, countryCode, pricingMode});
		return response.body.url;
	} catch (error) {
		logger.error('Localized card preapproval session creation failed', error);
		throw error;
	}
}

export async function continueLocalizedCardPreapproval(
	token: string,
): Promise<LocalizedCardPreapprovalContinueResponse> {
	try {
		const response = await http.post<LocalizedCardPreapprovalContinueResponse>(
			Endpoints.STRIPE_CHECKOUT_SUBSCRIPTION_PREAPPROVAL_CONTINUE,
			{token},
		);
		logger.debug('Localized card preapproval continuation polled', response.body);
		return response.body;
	} catch (error) {
		logger.error('Localized card preapproval continuation failed', error);
		throw error;
	}
}

export async function cancelSubscriptionAtPeriodEnd(): Promise<void> {
	try {
		await http.post({url: Endpoints.PREMIUM_CANCEL_SUBSCRIPTION});
		logger.info('Subscription set to cancel at period end');
	} catch (error) {
		logger.error('Failed to cancel subscription at period end', error);
		throw error;
	}
}

export async function reactivateSubscription(): Promise<void> {
	try {
		await http.post({url: Endpoints.PREMIUM_REACTIVATE_SUBSCRIPTION});
		logger.info('Subscription reactivated');
	} catch (error) {
		logger.error('Failed to reactivate subscription', error);
		throw error;
	}
}

export async function changeSubscriptionBillingCycle(billingCycle: 'monthly' | 'yearly'): Promise<void> {
	try {
		await http.post(Endpoints.PREMIUM_CHANGE_SUBSCRIPTION, {billing_cycle: billingCycle});
		logger.info('Subscription billing cycle changed', {billingCycle});
	} catch (error) {
		logger.error('Failed to change subscription billing cycle', error);
		throw error;
	}
}

export async function rejoinVisionaryGuild(): Promise<void> {
	try {
		await http.post({url: Endpoints.PREMIUM_VISIONARY_REJOIN});
		logger.info('Visionary guild rejoin requested');
	} catch (error) {
		logger.error('Failed to rejoin Visionary guild', error);
		throw error;
	}
}

export async function rejoinOperatorGuild(): Promise<void> {
	try {
		await http.post({url: Endpoints.PREMIUM_OPERATOR_REJOIN});
		logger.info('Operator guild rejoin requested');
	} catch (error) {
		logger.error('Failed to rejoin Operator guild', error);
		throw error;
	}
}
